import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Minimize2, Send } from 'lucide-react';
import axios from 'axios';
import { SECTION_AGENTS } from './sectionAgentConfig';
import MicButton from './MicButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EmbeddedAgentChat({ section, open, onClose }) {
  const cfg = SECTION_AGENTS[section];
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (cfg && messages.length === 0 && open) {
      setMessages([{ role: 'assistant', content: `¡Hola! Soy ${cfg.name} ${cfg.emoji}. ${cfg.tagline}. ¿En qué te ayudo?` }]);
    }
  }, [open, cfg, messages.length]);

  const send = useCallback(async (txt) => {
    const text = (txt || input).trim();
    if (!text || sending) return;
    setMessages(m => [...m, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    try {
      const { getToken } = await import('../utils/embedAuth');
      const token = getToken() || localStorage.getItem('token');
      const res = await axios.post(`${API}/chat/section`, {
        message: text,
        system_override: cfg?.system,
        section_context: section,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 60000, withCredentials: true });
      setMessages(m => [...m, { role: 'assistant', content: res.data?.response || 'Lo siento, no pude responder.' }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: '⚠️ No pude conectarme. Intenta de nuevo.' }]);
    } finally { setSending(false); }
  }, [input, sending, cfg, section]);

  if (!cfg || !open) return null;

  return (
    <div style={{
      position: 'fixed', right: 20, bottom: 90, zIndex: 9999,
      width: minimized ? 280 : 380, maxWidth: 'calc(100vw - 40px)',
      height: minimized ? 56 : 520, maxHeight: 'calc(100vh - 120px)',
      background: '#1e293b', borderRadius: 16, overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      border: '1px solid rgba(255,255,255,0.1)',
      display: 'flex', flexDirection: 'column', transition: 'all 0.25s',
    }}>
      <div className={`bg-gradient-to-r ${cfg.color} px-4 py-3 flex items-center gap-2`}>
        <div className="text-2xl select-none">{cfg.emoji}</div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-sm truncate">{cfg.name}</div>
          {!minimized && <div className="text-white/80 text-xs truncate">{cfg.tagline}</div>}
        </div>
        <button onClick={() => setMinimized(m => !m)} className="text-white/90 hover:text-white p-1" title="Minimizar">
          <Minimize2 size={16} />
        </button>
        <button onClick={onClose} className="text-white/90 hover:text-white p-1" title="Cerrar">
          <X size={18} />
        </button>
      </div>

      {!minimized && (
        <>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%', padding: '8px 12px', borderRadius: 14,
                background: m.role === 'user' ? '#2563eb' : 'rgba(255,255,255,0.08)',
                color: '#fff', fontSize: 13.5, lineHeight: 1.45, whiteSpace: 'pre-wrap',
              }}>
                {m.content}
              </div>
            ))}
            {sending && <div style={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>Pensando…</div>}
            <div ref={endRef} />
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: 8, display: 'flex', gap: 6 }}>
            <MicButton section={section} onResult={txt => send(txt)} />
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              placeholder={`Pregúntale a ${cfg.name.split(' ').slice(-1)[0]}…`}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff', borderRadius: 10, padding: '8px 12px', fontSize: 13, outline: 'none',
              }} />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              style={{
                background: sending || !input.trim() ? '#475569' : '#10b981',
                color: '#fff', border: 'none', borderRadius: 10, padding: '0 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
              <Send size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
